export type NestedMultiSelectOption = {
  label: string;
  value: string;
  children?: Array<{ label: string; value: string }>;
};

export const getChildValues = (item: NestedMultiSelectOption): string[] =>
  item.children?.map((child) => child.value) ?? [];

export const getParentSelectionState = ({
  item,
  selectedValues,
}: {
  item: NestedMultiSelectOption;
  selectedValues: readonly string[];
}): boolean | 'indeterminate' => {
  const childValues = getChildValues(item);

  if (childValues.length === 0) {
    return selectedValues.includes(item.value);
  }

  const selectedChildCount = childValues.filter((childValue) =>
    selectedValues.includes(childValue),
  ).length;

  if (selectedChildCount === 0) {
    return selectedValues.includes(item.value);
  }

  if (selectedChildCount === childValues.length) {
    return true;
  }

  return 'indeterminate';
};

export const toggleParentSelection = ({
  item,
  selectedValues,
}: {
  item: NestedMultiSelectOption;
  selectedValues: readonly string[];
}): string[] => {
  const childValues = getChildValues(item);
  const currentValues = [...selectedValues];
  const parentSelectionState = getParentSelectionState({
    item,
    selectedValues: currentValues,
  });

  if (parentSelectionState === true) {
    return currentValues.filter(
      (selectedValue) =>
        selectedValue !== item.value && !childValues.includes(selectedValue),
    );
  }

  if (parentSelectionState === 'indeterminate') {
    return [...new Set([...currentValues, ...childValues])];
  }

  return [...new Set([...currentValues, item.value])];
};

export const toggleChildSelection = ({
  itemValue,
  parentItem,
  selectedValues,
}: {
  itemValue: string;
  parentItem: NestedMultiSelectOption;
  selectedValues: readonly string[];
}): string[] => {
  const currentValues = [...selectedValues];
  const isSelected = currentValues.includes(itemValue);
  let nextValues = isSelected
    ? currentValues.filter((selectedValue) => selectedValue !== itemValue)
    : [...currentValues, itemValue];

  if (!isSelected) {
    nextValues = nextValues.filter(
      (selectedValue) => selectedValue !== parentItem.value,
    );
  }

  return nextValues;
};

export const toggleLeafSelection = ({
  itemValue,
  selectedValues,
}: {
  itemValue: string;
  selectedValues: readonly string[];
}): string[] => {
  const currentValues = [...selectedValues];
  const isSelected = currentValues.includes(itemValue);

  return isSelected
    ? currentValues.filter((selectedValue) => selectedValue !== itemValue)
    : [...currentValues, itemValue];
};

export const findOptionLabel = ({
  items,
  value,
}: {
  items: readonly NestedMultiSelectOption[];
  value: string;
}): string | undefined => {
  for (const item of items) {
    if (item.value === value) {
      return item.label;
    }

    const matchingChild = item.children?.find((child) => child.value === value);

    if (matchingChild) {
      return matchingChild.label;
    }
  }

  return undefined;
};

export const applyNestedSelectChange = ({
  previous,
  next,
  items,
}: {
  previous: readonly string[];
  next: readonly string[];
  items: readonly NestedMultiSelectOption[];
}): string[] => {
  const added = next.filter((value) => !previous.includes(value));
  const removed = previous.filter((value) => !next.includes(value));

  if (added.length === 1 && removed.length === 0) {
    const value = added[0];
    const parentItem = items.find(
      (item) => item.value === value && (item.children?.length ?? 0) > 0,
    );

    if (parentItem) {
      return toggleParentSelection({
        item: parentItem,
        selectedValues: previous,
      });
    }

    const parentOfChild = items.find((item) =>
      item.children?.some((child) => child.value === value),
    );

    if (parentOfChild) {
      return toggleChildSelection({
        itemValue: value,
        parentItem: parentOfChild,
        selectedValues: previous,
      });
    }

    return [...next];
  }

  if (removed.length === 1 && added.length === 0) {
    const value = removed[0];
    const parentItem = items.find(
      (item) => item.value === value && (item.children?.length ?? 0) > 0,
    );

    if (parentItem) {
      return toggleParentSelection({
        item: parentItem,
        selectedValues: previous,
      });
    }

    const parentOfChild = items.find((item) =>
      item.children?.some((child) => child.value === value),
    );

    if (parentOfChild) {
      return toggleChildSelection({
        itemValue: value,
        parentItem: parentOfChild,
        selectedValues: previous,
      });
    }

    return [...next];
  }

  return [...next];
};

export const getSyntheticSelectedParentValues = ({
  selectedValues,
  items,
}: {
  selectedValues: readonly string[];
  items: readonly NestedMultiSelectOption[];
}): string[] =>
  items
    .filter((item) => (item.children?.length ?? 0) > 0)
    .filter(
      (item) =>
        getParentSelectionState({ item, selectedValues }) === true &&
        !selectedValues.includes(item.value),
    )
    .map((item) => item.value);

export const sanitizeNestedSelectValues = ({
  values,
  items,
}: {
  values: readonly string[];
  items: readonly NestedMultiSelectOption[];
}): string[] => {
  const childValuesByParent = items
    .filter((item) => (item.children?.length ?? 0) > 0)
    .map((item) => ({
      parentValue: item.value,
      childValues: getChildValues(item),
    }));

  return values.filter((value) => {
    const parent = childValuesByParent.find(
      ({ parentValue }) => parentValue === value,
    );

    if (!parent) {
      return true;
    }

    const hasSelectedChildren = parent.childValues.some((childValue) =>
      values.includes(childValue),
    );

    return !hasSelectedChildren;
  });
};
